import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { getErrorTrackingObject } from '@rci/self-serve-fido/core/analytics';
import { AuthenticationService } from '@rci/self-serve-fido/shared/authentication/authentication.service';
import { AccountDetailService } from '@rci/self-serve-fido/state/account/account-detail.service';
import { AccountHelper } from '@rci/self-serve-fido/state/account/account.helper';
import { UserInformationStateFacade } from '@rci/self-serve-fido/state/user-information/user-information-state.facade';
import { ApiEndpointService } from '@rci/self-serve-shared';
import { StateService } from '@rci/state-service';
import { CommonAppConfig, CustomHttpErrorResponse, UserAccount } from '@rogers/self-serve-common';
import { AccountDetail, AccountSubscription, ServiceTypeEnum, SubTypeEnum } from '@rogers/self-serve-common/account';
import { getLatestValue } from '@rogers/self-serve-utils';
import { map } from 'rxjs/operators';
import { modalWindowConfig } from '../modal-window.config';
import { ViewContainerRefService } from '../view-container-ref/view-container-ref.service';
import { PHONE_NUMBER } from '@rci/self-serve-fido/app.constant';
import { LOCAL_STORAGE } from '@rogers/self-serve-utils/storage';

@Injectable()
export class CommonAppConfigFactory implements CommonAppConfig {
  private readonly apiEndpointService = inject(ApiEndpointService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly stateService = inject(StateService);
  private readonly viewContainerRefService = inject(ViewContainerRefService);
  private readonly translateService = inject(TranslateService);
  private readonly userInformationStateFacade = inject(UserInformationStateFacade);
  private readonly accountDetailService = inject(AccountDetailService);
  private readonly localStorage = inject<Storage>(LOCAL_STORAGE);

  get accountsApiUrl() {
    return this.apiEndpointService.accountsBaseUrl;
  }

  get accountDetail$() {
    return this.accountDetailService.getSelectedAccountDetail$().pipe(
      map(selectedAccount => {
        const subscriptionDetails = selectedAccount.subscriptions.find(
          sub => sub.subNumber === this.currentPhoneNumber,
        );
        return {
          accountNumber: selectedAccount.accountNumber,
          accountAlias: selectedAccount.alias,
          subscription: {
            firstName: subscriptionDetails?.firstName,
            lastName: subscriptionDetails?.lastName,
            alias: subscriptionDetails?.alias,
            isLostOrStolen: subscriptionDetails?.isLostOrStolen,
          },
        };
      }),
    );
  }

  get baseApiUrl() {
    return this.apiEndpointService.accountsMsBaseUrl;
  }

  get baseMServicesUrl() {
    return this.apiEndpointService.baseMServicesUrl;
  }

  readonly clientId = 'Fido Web';

  get contentBaseUrl() {
    return this.apiEndpointService.contentBaseUrl;
  }

  get contentApiUrlUnauthenticated() {
    return this.apiEndpointService.contentApiUrlUnauthenticated;
  }

  get currentPhoneNumber() {
    return this.localStorage.getItem(PHONE_NUMBER);
  }

  get financedAccessoriesBaseUrl() {
    return this.apiEndpointService.financedAccessoriesBaseUrl;
  }

  get language() {
    return this.translateService.currentLang;
  }

  get modalWindowConfig() {
    return modalWindowConfig;
  }

  get province$() {
    return this.stateService.province$;
  }

  get selectedAccountNumber() {
    return this.userInformationStateFacade.selectedAccountNumber;
  }

  get selectedAccountNumber$() {
    return this.userInformationStateFacade.selectedAccountNumber$;
  }

  get viewContainerRef() {
    return this.viewContainerRefService.viewContainerRef;
  }

  get wirelessApiUrl() {
    return this.apiEndpointService.wirelessBaseUrl;
  }

  getBrandAccountDetail(subscriptions: AccountSubscription[], _accountDetail: AccountDetail, account: UserAccount) {
    if (!subscriptions || subscriptions.length === 0) {
      return {
        showVasBadge: false,
        transformedSubscriptions: [],
        hasActiveWirelessSubscription: false,
        hasWirelessSubscription: false,
        hasResidentialSubscription: false,
        hasActiveResidentialSubscription: false,
      };
    }

    const transformedSubscriptions = this.getTransformedSubscriptions(subscriptions, account);
    const hasActiveWirelessSubscription = this.hasActiveWirelessSubscription(subscriptions);
    const hasWirelessSubscription = this.hasWirelessSubscription(subscriptions);
    const hasResidentialSubscription = this.hasResidentialSubscription(subscriptions);
    const hasActiveResidentialSubscription = this.hasActiveResidentialSubscription(subscriptions);
    return {
      showVasBadge: false,
      transformedSubscriptions,
      hasActiveWirelessSubscription,
      hasWirelessSubscription,
      hasResidentialSubscription,
      hasActiveResidentialSubscription,
    };
  }

  getHeaders() {
    return this.authenticationService.getDomainServiceHeaders(getLatestValue(this.stateService.language$));
  }

  getErrorTrackingObject(error: CustomHttpErrorResponse) {
    return getErrorTrackingObject(error);
  }

  fetchAccountDetail$(accountNumber, accountAlias, accountTypeNumber) {
    return this.accountDetailService.fetchAccountDetail$(accountNumber, accountAlias, accountTypeNumber);
  }

  setSelectedAccountNumber(accountNumber: string) {
    this.userInformationStateFacade.updateSelectedAccountNumber(accountNumber);
  }

  setSelectedSubscriptionNumber(subscriptionNumber: string) {
    this.userInformationStateFacade.updateSelectedSubscriberNo(subscriptionNumber);
  }

  transformAccountList(accounts: UserAccount[]): UserAccount[] {
    return AccountHelper.transformAccountList(accounts);
  }

  private hasWirelessSubscription(subs: AccountSubscription[]) {
    return subs.some(sub => sub.type === ('CTN' as SubTypeEnum));
  }

  private hasActiveWirelessSubscription(subs) {
    return subs.some(sub => sub.type === ('CTN' as SubTypeEnum) && sub.isActive);
  }

  private hasResidentialSubscription(subs) {
    return subs.some(sub => sub.serviceType === (1 as ServiceTypeEnum));
  }

  private hasActiveResidentialSubscription(subs) {
    return subs.some(sub => sub.serviceType === (1 as ServiceTypeEnum) && sub.isActive);
  }

  private getTransformedSubscriptions(subscriptions: AccountSubscription[], account: UserAccount) {
    const activeSubscriptions = subscriptions.filter(subscription => subscription.isActive);
    const suspendedSubscriptions = subscriptions.filter(subscription => subscription.isSuspended);
    return [...activeSubscriptions, ...suspendedSubscriptions].map(subscription => ({
      ...subscription,
      isLostOrStolen: subscription.reason === 'LOS',
      overageProtectionProfileLink:
        `${this.apiEndpointService.wirelessBaseUrl}` +
        `${account.alias}/subscription/${subscription.alias}/data-overage-protection`,
    }));
  }
}
